<script setup>
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import ExportButton from "@js/components/buttons/ExportButton.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const {
  tenant,
  indexed = undefined,
  notIndexed = undefined,
} = defineProps({
  tenant: {
    required: true,
    type: Object,
  },
  indexed: {
    required: false,
    type: Boolean,
  },
  notIndexed: {
    required: false,
    type: Boolean,
  },
});

const { t } = useI18n();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  selectionIds,
  exportData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "seoIndexationSitemaps.name",
      header: () => t("label.sitemap"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.seoIndexationSitemap.name,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "seoIndexationUrls.url",
      header: () => t("label.url"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.url,
          href: props.row.original.url,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "coverageStateId",
      header: () => t("label.status"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.googleSearchConsoleCoverageState
            .translatedLabel,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "indexedAt",
      header: () => t("label.indexedAt"),
      cell: (props) =>
        props.row.original.indexedAt
          ? h(TextCellV2, {
            text: props.row.original.indexedAt,
          })
          : undefined,
    }),
    columnHelper.accessor(() => null, {
      id: "notIndexedAt",
      header: () => t("label.notIndexedAt"),
      cell: (props) =>
        props.row.original.notIndexedAt
          ? h(TextCellV2, {
            text: props.row.original.notIndexedAt,
          })
          : undefined,
    }),
    columnHelper.accessor(() => null, {
      id: "updatedAt",
      header: () => t("label.updatedAt"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.updatedAt,
        }),
    }),
  ],
  url: route("front.projectManagement.seo.postIndexationUrls", {
    tenant: tenant.id,
  }),
  exportUrl: route("front.projectManagement.seo.postExportIndexationUrls", {
    tenant: tenant.id,
  }),
  data: {
    indexed,
    notIndexed,
  },
  selectionIdKey: "id",
  exportSelectionIdKey: "seoIndexationUrls.id",
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :set-global-filter="setGlobalFilter"
    :available-filters="availableFilters"
    :filters="filters"
    :set-filters="setFilters"
  >
    <template #actions>
      <ExportButton
        :csv-export-callback="() => exportData('csv')"
        :xlsx-export-callback="() => exportData('xlsx')"
        :disabled="selectionIds.length === 0"
      />
    </template>
  </DataTableLayout>
</template>
